import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Info, Bell, CalendarClock, AlertTriangle, X } from 'lucide-react';
import { planApi } from '../api/tenants.js';
import { Card } from './ui/Card.jsx';
import { Button } from './ui/Button.jsx';
import { overlayDismiss } from '../lib/overlayDismiss';
import { useTenant } from './TenantContext.jsx';

/**
 * 加值分鐘數（預付帳號）客戶端畫面 —— 規格：PM 簡報「商用帳號數據頁」幾張。
 *   PrepaidBilling     本月計費卡片（預付版）：總額度 = 免費 + 加購、兩條用量條、最近到期提示、按鈕
 *   HeaderMinuteChip   頁首「剩餘分鐘數 ⓘ」：點數字回通話數據頁，點 ⓘ 開說明
 *   MinuteInfoModal    ⓘ 說明視窗（分鐘用量的說明）
 *   UsageAlertModal    用量預估與通知視窗
 * 數字全部來自後端 /me/minute-balance（與通話守門同一份計算），這裡不自己算扣抵。
 */
const TZ = 8 * 3600 * 1000;
const todayTaipei = () => new Date(Date.now() + TZ).toISOString().slice(0, 10);
export const daysUntil = (day) => Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${todayTaipei()}T00:00:00Z`)) / 86400000);
const num = (n) => Number(n || 0).toLocaleString();
const LIGHT = { green: 'bg-success-500/15 text-success-500', yellow: 'bg-warn-100 text-warn-500', red: 'bg-danger-500/15 text-danger-500' };

/** 最快到期、還有剩的那包（已過期的不算） */
export function nearestPack(balance) {
    return (balance?.packs || []).filter((p) => !p.expired && p.remaining > 0).sort((a, b) => a.expiryDay.localeCompare(b.expiryDay))[0] || null;
}

// 一律 portal 到 body：頁首（Layout header）有 backdrop-blur，會讓裡面 position:fixed 的子元素
// 改以 header 為定位基準 → 從頁首「剩餘分鐘數 ⓘ」打開時視窗卡在頁首、遮罩只蓋到頁首那條。
function Modal(props) {
    return createPortal(<ModalBody {...props} />, document.body);
}

function ModalBody({ title, onClose, children, wide = false, narrow = false, headerExtra = null }) {
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/60 p-4" {...overlayDismiss(onClose)}>
            <div className={`w-full ${narrow ? 'max-w-md' : wide ? 'max-w-3xl' : 'max-w-2xl'} max-h-[90vh] flex flex-col rounded-xl bg-cream-50 border border-cream-200 shadow-xl`} role="dialog" aria-modal="true">
                <div className="px-6 py-4 flex items-center gap-3 border-b border-cream-200 flex-wrap">
                    <h2 className="heading-display text-lg font-semibold text-ink-900">{title}</h2>
                    {headerExtra}
                    <button onClick={onClose} aria-label="close" className="ml-auto p-1 rounded text-ink-400 hover:text-ink-700 hover:bg-cream-100 focus-visible:outline-2 focus-visible:outline-accent-500"><X size={16} /></button>
                </div>
                <div className="px-6 py-5 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}

/** 扣抵規則的 4 點說明（說明視窗與分鐘數紀錄頁共用，文字照簡報） */
export function MinuteRuleNotes() {
    const { t } = useTranslation('dashboard');
    return (
        <ul className="list-disc pl-5 space-y-1 text-xs text-ink-700 leading-relaxed">
            {[1, 2, 3, 4].map((i) => <li key={i}>{t(`minutePack.rules.n${i}`)}</li>)}
        </ul>
    );
}

/** 外撥「分鐘數已用完」提示（取代瀏覽器原生 alert）：恢復被擋、啟動時提醒都用這個 */
export function QuotaModal({ text, onClose }) {
    const { t } = useTranslation(['outbound', 'dashboard']);
    const nav = useNavigate();
    return (
        <Modal title={t('outbound:quotaModal.title')} onClose={onClose} narrow>
            <div className="flex items-start gap-3 text-sm text-ink-700">
                <AlertTriangle size={18} className="shrink-0 mt-0.5 text-danger-500" />
                <p>{text}</p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={() => nav('/dashboard/minutes')}>{t('dashboard:minutePack.recordsBtn')}</Button>
                <Button onClick={onClose}>{t('outbound:quotaModal.ok')}</Button>
            </div>
        </Modal>
    );
}

export function MinuteInfoModal({ onClose }) {
    const { t } = useTranslation('dashboard');
    const steps = [
        ['bg-success-500/10 border-success-500/30', 'bg-success-500'],
        ['bg-warn-100/70 border-warn-500/30', 'bg-[#8a6230]'],
        ['bg-danger-500/10 border-danger-500/30', 'bg-accent-700'],
    ];
    return (
        <Modal title={t('minutePack.info.title')} onClose={onClose} wide>
            <p className="text-sm text-ink-700 mb-4">{t('minutePack.info.subtitle')}</p>
            <div className="text-base font-semibold text-ink-900 mb-3">{t('minutePack.info.heading')}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                {steps.map(([box, dot], i) => (
                    <div key={i} className={`rounded-lg border p-4 text-center ${box}`}>
                        <span className={`inline-grid place-items-center size-7 rounded-full text-white text-sm font-semibold ${dot}`}>{i + 1}</span>
                        <div className="mt-2 font-semibold text-ink-900">{t(`minutePack.info.s${i + 1}.title`)}</div>
                        <div className="mt-1 text-xs text-ink-700 leading-relaxed">{t(`minutePack.info.s${i + 1}.desc`)}</div>
                        {i === 2 && <div className="mt-2 px-2 py-1.5 rounded border border-dashed border-danger-500/40 bg-white/70 text-[11px] text-accent-700">「{t('minutePack.info.voice')}」</div>}
                        <span className="mt-3 inline-block px-2.5 py-0.5 rounded-full bg-white border border-cream-300 text-[11px] text-ink-700">{t(`minutePack.info.s${i + 1}.tag`)}</span>
                    </div>
                ))}
            </div>
            <div className="mb-5 text-xs text-ink-500">{t('minutePack.info.ratio')}</div>
            <MinuteRuleNotes />
        </Modal>
    );
}

export function UsageAlertModal({ balance, onClose, onSaved }) {
    const { t } = useTranslation('dashboard');
    const { hostId } = useTenant();
    const [alert, setAlert] = useState(null);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    useEffect(() => { planApi.usageAlert(hostId).then((r) => setAlert(r.usageAlert)).catch((e) => setMsg({ ok: false, text: e.message })); }, [hostId]);
    const fc = balance?.forecast;
    const save = async (next) => {
        setBusy(true); setMsg(null);
        try { const r = await planApi.saveUsageAlert(hostId, next); setAlert(r.usageAlert); setMsg({ ok: true, text: t('minutePack.alert.saved') }); onSaved?.(r.usageAlert); }
        catch (e) { setMsg({ ok: false, text: e.message }); }
        finally { setBusy(false); }
    };
    const addEmails = () => {
        const add = draft.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
        if (!add.length || !alert) return;
        setDraft('');
        save({ ...alert, emails: [...new Set([...(alert.emails || []), ...add])] });
    };
    const sendTest = async () => {
        setBusy(true); setMsg(null);
        try { await planApi.testUsageAlert(hostId); setMsg({ ok: true, text: t('minutePack.alert.testSent') }); }
        catch (e) { setMsg({ ok: false, text: e.message }); }
        finally { setBusy(false); }
    };
    const configured = !!(alert && (alert.emails || []).length);
    return (
        <Modal
            title={t('minutePack.fc.title', { month: balance?.month || '' })}
            onClose={onClose}
            headerExtra={<>
                {fc?.light && <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${LIGHT[fc.light]}`}>{t(`minutePack.light.${fc.light}`)}</span>}
                {fc && <span className="font-mono text-[10px] tracking-[0.2em] text-ink-400">DAY {fc.dayOfMonth} / {fc.daysInMonth}</span>}
            </>}
        >
            {fc && (
                <div className="pb-4 mb-4 border-b border-cream-200">
                    <p className="text-sm text-ink-900">{t(`minutePack.fc.${fc.light || 'green'}`, { projected: num(fc.projected) })}</p>
                    <p className="mt-1 text-[11px] text-ink-400">{t('minutePack.fc.method', { daily: fc.daily, days: fc.remainingDays, used: num(balance.usedMinutes) })}</p>
                </div>
            )}
            {alert && (
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Bell size={14} className="text-ink-500" />
                        <span className="text-sm font-medium text-ink-900">{t('minutePack.alert.title')}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${configured ? 'bg-success-500/15 text-success-500' : 'bg-warn-100 text-warn-500'}`}>{configured ? t('minutePack.alert.set') : t('minutePack.alert.notSet')}</span>
                    </div>
                    <p className="text-[11px] text-ink-400 mb-3">{t('minutePack.alert.desc')}</p>
                    <label className="flex items-center gap-2 text-xs text-ink-700 mb-4 flex-wrap">
                        <input type="checkbox" checked={!!alert.enabled} disabled={busy} onChange={(e) => save({ ...alert, enabled: e.target.checked })} />
                        {t('minutePack.alert.reach')}
                        <input type="number" min="1" max="100" value={alert.thresholdPct} disabled={busy}
                            onChange={(e) => setAlert({ ...alert, thresholdPct: e.target.value })} onBlur={() => save(alert)}
                            className="w-16 px-2 py-1 border border-cream-300 rounded tnum" />
                        {t('minutePack.alert.reachSuffix')}
                    </label>
                    <div className="text-xs font-medium text-ink-900 mb-1">{t('minutePack.alert.emailsLabel')}</div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {(alert.emails || []).length === 0 && <span className="text-xs text-ink-400 italic">{t('minutePack.alert.noEmail')}</span>}
                        {(alert.emails || []).map((em) => (
                            <span key={em} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cream-100 text-xs text-ink-700">
                                {em}
                                <button type="button" disabled={busy} aria-label={t('minutePack.alert.remove', { email: em })} onClick={() => save({ ...alert, emails: alert.emails.filter((x) => x !== em) })}>×</button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2 flex-wrap mb-4">
                        <input type="text" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addEmails(); }}
                            placeholder={t('minutePack.alert.emailPlaceholder')} className="flex-1 min-w-[220px] px-3 py-1.5 border border-cream-300 rounded text-xs bg-white" />
                        <button type="button" disabled={busy || !draft.trim()} onClick={addEmails} className="px-3 py-1.5 rounded border border-cream-300 bg-white text-xs text-ink-900 disabled:opacity-40">{t('minutePack.alert.add')}</button>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap text-xs text-ink-700">
                        <span className="font-medium text-ink-900">{t('minutePack.alert.when')}</span>
                        {['onYellow', 'onRed', 'onExhausted'].map((k) => (
                            <label key={k} className="inline-flex items-center gap-1.5">
                                <input type="checkbox" checked={k === 'onYellow' ? !!alert[k] : alert[k] !== false} disabled={busy} onChange={(e) => save({ ...alert, [k]: e.target.checked })} />
                                {t(`minutePack.alert.${k}`)}
                            </label>
                        ))}
                        <button type="button" disabled={busy || !configured} onClick={sendTest} className="ml-auto px-3 py-1.5 rounded border border-cream-300 bg-white text-xs text-ink-900 disabled:opacity-40">{t('minutePack.alert.test')}</button>
                    </div>
                    {msg && <div className={`mt-3 text-xs ${msg.ok ? 'text-success-500' : 'text-danger-500'}`} role="status">{msg.text}</div>}
                </div>
            )}
        </Modal>
    );
}

function Bar({ label, used, total, thresholdPct, tone, emptyText }) {
    const { t } = useTranslation('dashboard');
    const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
    return (
        <div className="mb-5">
            <div className="flex items-baseline justify-between mb-1.5 text-xs">
                <span className="font-medium text-ink-900">{label}</span>
                <span className="tnum text-ink-500">{total > 0 ? t('minutePack.usedOf', { used: num(used), total: num(total), pct: Math.round(pct) }) : emptyText}</span>
            </div>
            <div className="relative h-2.5 rounded-full bg-cream-100">
                <div className={`absolute inset-y-0 left-0 rounded-full ${tone}`} style={{ width: `${pct}%` }} />
                <div className="absolute -top-1 -bottom-1 w-px bg-ink-700" style={{ left: `${thresholdPct}%` }} />
            </div>
            <div className="relative h-4 text-[10px] text-ink-400">
                <span className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${thresholdPct}%` }}>{t('minutePack.thresholdMark', { pct: thresholdPct })}</span>
            </div>
        </div>
    );
}

/** 本月計費卡片（預付帳號版）。後付帳號仍用 DashboardPage 原本的 BillingPanel。 */
export function PrepaidBilling({ balance, planName, planId }) {
    const { t } = useTranslation('dashboard');
    const { hostId } = useTenant();
    const nav = useNavigate();
    const [modal, setModal] = useState(null);       // 'alert' | 'info'
    const [thresholdPct, setThresholdPct] = useState(80);
    const [alertSet, setAlertSet] = useState(null);
    useEffect(() => {
        planApi.usageAlert(hostId).then((r) => { setThresholdPct(r.usageAlert.thresholdPct || 80); setAlertSet((r.usageAlert.emails || []).length > 0); }).catch(() => {});
    }, [hostId]);
    const included = balance.includedMinutes || 0;
    const packTotal = balance.packTotal || 0;
    const packLeft = Math.max(0, balance.packBalance || 0);
    const total = included + packTotal;
    const used = balance.usedMinutes || 0;
    const fc = balance.forecast;
    const near = nearestPack(balance);
    const hadPacks = (balance.packs || []).length > 0;
    return (
        <Card className="mb-10">
            {balance.exhausted && (
                <div className="mb-5 px-4 py-3 rounded-md border border-danger-500/40 bg-danger-500/10 text-sm text-ink-900 flex items-center gap-2">
                    <AlertTriangle size={15} className="text-danger-500 shrink-0" />
                    <span><span className="font-semibold">{t('minutePack.exhausted.title')}</span><span className="ml-2 text-ink-700">{t('minutePack.exhausted.desc')}</span></span>
                </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">billing · {balance.month}</span>
                {fc && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">day {fc.dayOfMonth} / {fc.daysInMonth}・remaining {fc.remainingDays}d</span>}
            </div>
            <div className="flex items-baseline gap-3 flex-wrap mb-5">
                <h2 className="heading-display text-2xl font-semibold text-ink-900">{t('billing.title')}</h2>
                {planName && <span className="px-2.5 py-0.5 rounded-full bg-accent-50 text-accent-700 text-[11px] font-medium">{planName} <span className="font-mono text-[9px] opacity-60 uppercase">{planId}</span></span>}
            </div>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                <div className="tnum">
                    <span className="heading-display text-[40px] font-semibold text-ink-900 leading-none">{num(used)}</span>
                    <span className="font-mono text-[11px] text-ink-400 ml-1.5 uppercase tracking-[0.18em]">min</span>
                    <span className="text-ink-300 mx-2.5">/</span>
                    <span className="text-2xl font-medium text-ink-500">{num(total)}</span>
                    <span className="font-mono text-[10px] text-ink-400 ml-1.5 uppercase tracking-[0.18em]">min・{total > 0 ? Math.round((used / total) * 100) : 0}%</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {fc?.light && <span className={`px-2 py-1 rounded text-[11px] font-medium ${LIGHT[fc.light]}`}>{t(`minutePack.light.${fc.light}`)}</span>}
                    <button type="button" onClick={() => setModal('alert')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ink-900 text-cream-50 text-xs hover:bg-ink-700">
                        <Bell size={13} />{t('minutePack.alertBtn')}{alertSet === false && <span className="opacity-70">（{t('minutePack.alert.notSet')}）</span>}
                    </button>
                </div>
            </div>
            <Bar label={t('minutePack.free')} used={Math.min(used, included)} total={included} thresholdPct={thresholdPct} tone="bg-success-500" emptyText="—" />
            <Bar label={t('minutePack.pack')} used={packTotal - packLeft} total={packTotal} thresholdPct={thresholdPct} tone="bg-accent-500"
                emptyText={hadPacks ? t('minutePack.packExpired') : t('minutePack.packNone')} />
            {(near || hadPacks) && (
                <div className="mb-4 px-4 py-2.5 rounded-md border border-warn-500/30 bg-warn-100/50 flex items-center gap-3 flex-wrap text-xs text-ink-900">
                    <AlertTriangle size={14} className="text-warn-500 shrink-0" />
                    <span>{near ? t('minutePack.nearest', { date: near.expiryDay, minutes: num(near.remaining), days: daysUntil(near.expiryDay) }) : t('minutePack.noLivePack')}</span>
                    <button type="button" onClick={() => nav('/dashboard/minutes')} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ink-900 text-cream-50 hover:bg-ink-700">
                        <CalendarClock size={13} />{t('minutePack.recordsBtn')}
                    </button>
                </div>
            )}
            {balance.packBalance < 0 && <div className="mb-2 text-xs text-accent-700">{t('minutePack.debt', { count: num(Math.abs(balance.packBalance)) })}</div>}
            <div className="flex items-center gap-4 text-xs text-ink-500">
                <span className="inline-flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-success-500" />{t('minutePack.phone', { n: num(balance.phoneMinutes) })}</span>
                <span className="inline-flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-accent-500" />{t('minutePack.web', { n: num(balance.webMinutes) })}</span>
                <button type="button" onClick={() => setModal('info')} className="ml-auto inline-flex items-center gap-1 text-ink-500 hover:text-ink-900"><Info size={13} />{t('minutePack.info.title')}</button>
            </div>
            {modal === 'alert' && <UsageAlertModal balance={balance} onClose={() => setModal(null)} onSaved={(a) => { setThresholdPct(a.thresholdPct); setAlertSet((a.emails || []).length > 0); }} />}
            {modal === 'info' && <MinuteInfoModal onClose={() => setModal(null)} />}
        </Card>
    );
}

/** 頁首「剩餘分鐘數 ⓘ」：只有預付帳號顯示。點數字回通話數據頁，點 ⓘ 開說明。 */
export function HeaderMinuteChip() {
    const { t } = useTranslation('dashboard');
    const { hostId } = useTenant();
    const nav = useNavigate();
    const [b, setB] = useState(null);
    const [info, setInfo] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setB(null);   // 切租戶先清掉，免得短暫顯示上一個租戶的分鐘數
        const load = () => planApi.minuteBalance(hostId).then((r) => { if (!cancelled) setB(r.balance); }).catch(() => {});
        load();
        const id = setInterval(load, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(id); };
    }, [hostId]);
    if (b?.billingMode !== 'prepaid') return null;
    return (
        <>
            <span className={`inline-flex items-center rounded-full border text-xs ${b.exhausted ? 'border-danger-500/50 bg-danger-500/10' : 'border-cream-300 bg-white'}`}>
                <button type="button" onClick={() => nav('/dashboard')} className="pl-3 pr-1.5 py-1.5 text-ink-700 hover:text-ink-900">
                    {t('minutePack.chip')}<span className={`ml-1 font-semibold tnum ${b.exhausted ? 'text-danger-500' : 'text-ink-900'}`}>{num(Math.max(0, b.available || 0))}</span>
                    {b.activeCalls > 0 && <span className="ml-1.5 text-ink-500">· {t('minutePack.active', { n: b.activeCalls })}</span>}
                </button>
                <button type="button" onClick={() => setInfo(true)} aria-label={t('minutePack.info.title')} className="pr-2.5 pl-1 py-1.5 text-ink-400 hover:text-ink-900"><Info size={14} /></button>
            </span>
            {info && <MinuteInfoModal onClose={() => setInfo(false)} />}
        </>
    );
}
